/* eslint-disable @typescript-eslint/no-unused-vars */
import { useRef, useEffect, useState } from 'react';
import { useRoutes, useLocation, useNavigate } from 'react-router-dom';
import { useDemoNavigate } from './hooks/useDemoNavigate';
import './App.css';
import { BottomNav } from './components/layout/BottomNav';
import { appRoutes } from './navigation/routes';
import { useAppContext } from './store/AppContext';
import { useSentryContext } from './hooks/useSentryContext';
import { syncAllPendingEntries, registerBackgroundSync } from './services/offlineSync.service';
import { UpdatePrompt } from './components/ui/UpdatePrompt';
import { onUpdateAvailable, applyUpdate } from './utils/serviceWorkerUpdate';
import { DemoBanner, PharmacyDemoBanner } from './demo';
import { isPharmacyDemo } from './demo/demoState';
import { AppSplash } from './components/ui/AppSplash';
import { UpgradePromptProvider } from './components/ui/UpgradePromptProvider';

// Boot performance markers
function bootMark(name: string) {
  if (performance?.mark) performance.mark(`boot:${name}`);
  if (!import.meta.env.PROD) console.log(`[Boot] ${name} @ ${Math.round(performance.now())}ms`);
}

function App() {
  const element = useRoutes(appRoutes);
  const location = useLocation();
  const navigate = useNavigate();
  const demoNavigate = useDemoNavigate();
  const isAuthScreen = location.pathname.startsWith('/auth');
  const isAuthAction = location.pathname === '/auth/action';

  const { user, isDemoMode, loading, onboardingStatus } = useAppContext();
  useSentryContext();

  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);

  // ============================================================
  // BRIEF §B2: Time-boxed splash overlay
  // - Min 250ms (prevent flicker)
  // - Max 800ms (prevent "stuck" perception)
  // - After max, ALWAYS render shell even if auth still resolving
  // ============================================================
  const [splashVisible, setSplashVisible] = useState(true);
  const [mountTime] = useState(Date.now());

  useEffect(() => {
    const elapsed = Date.now() - mountTime;
    const MIN_SPLASH = 250;
    const MAX_SPLASH = 800;

    if (!loading) {
      // Auth resolved — hide splash after MIN_SPLASH
      const remaining = Math.max(0, MIN_SPLASH - elapsed);
      const timer = setTimeout(() => setSplashVisible(false), remaining);
      return () => clearTimeout(timer);
    } else {
      // Auth still resolving — force hide at MAX_SPLASH
      const remaining = Math.max(0, MAX_SPLASH - elapsed);
      const timer = setTimeout(() => setSplashVisible(false), remaining);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Remove inline HTML splash + clear failsafe timeout on mount
  useEffect(() => {
    bootMark('react_mounted');

    if (window.__CORETRAK_SPLASH_TIMEOUT) {
      clearTimeout(window.__CORETRAK_SPLASH_TIMEOUT);
      window.__CORETRAK_SPLASH_TIMEOUT = undefined;
    }

    const preloadSplash = document.getElementById('preload-splash');
    if (preloadSplash) {
      preloadSplash.classList.add('fade-out');
      const timer = setTimeout(() => {
        preloadSplash.remove();
        document.getElementById('preload-splash-styles')?.remove();
      }, 350);
      return () => clearTimeout(timer);
    }
  }, []);

  // Boot markers for auth + first screen
  const authMarkedRef = useRef(false);
  useEffect(() => {
    if (!loading && !authMarkedRef.current) {
      authMarkedRef.current = true;
      bootMark('auth_ready');
    }
  }, [loading]);

  const screenMarkedRef = useRef(false);
  useEffect(() => {
    if (!splashVisible && !screenMarkedRef.current) {
      screenMarkedRef.current = true;
      requestAnimationFrame(() => {
        bootMark('first_screen_painted');
        if (!import.meta.env.PROD && performance?.getEntriesByName) {
          try {
            const get = (n: string) => {
              const e = performance.getEntriesByName(`boot:${n}`)[0];
              return e ? `${Math.round(e.startTime)}ms` : 'n/a';
            };
            console.log('[Boot Summary]', {
              splash: get('inline_splash_visible'),
              mounted: get('react_mounted'),
              auth: get('auth_ready'),
              screen: get('first_screen_painted'),
            });
          } catch { /* ignore */ }
        }
      });
    }
  }, [splashVisible]);

  // Navigation guards (unchanged)
  useEffect(() => {
    if (loading) return;
    if (!user && !isAuthScreen && !isDemoMode) {
      navigate('/auth/login');
    } else if (user && (isAuthScreen || location.pathname === '/') && !isAuthAction) {
      // Only route to /home if onboarding is confirmed complete.
      // If still loading or incomplete, let ProtectedRoute handle routing.
      if (onboardingStatus === 'complete') {
        navigate('/home');
      } else if (onboardingStatus === 'incomplete') {
        navigate('/onboarding');
      }
      // onboardingStatus === 'loading' → do nothing, wait for profile to settle
    }
  }, [user, loading, isAuthScreen, isAuthAction, navigate, location.pathname, onboardingStatus]);

  useEffect(() => {
    if (isDemoMode) console.log('[App] [ITEM 4] Demo mode active');
  }, [isDemoMode]);

  useEffect(() => {
    if (!import.meta.env.PROD) return;
    onUpdateAvailable(() => { setShowUpdatePrompt(true); });
  }, []);

  // ── Frequent foods background health-check ──
  // Runs once per session after auth, throttled to once per 7 days via localStorage.
  // Scans user's frequent foods for implausible macros and quarantines bad entries.
  useEffect(() => {
    if (isDemoMode || !user) return;
    void import('./services/frequentFoods.service').then(({ runFrequentFoodsHealthCheck }) => {
      void runFrequentFoodsHealthCheck(user.uid);
    }).catch(() => { /* non-critical */ });
  }, [user, isDemoMode]);

  useEffect(() => {
    if (isDemoMode || !user) return;

    const handleOnline = async () => {
      try {
        const result = await syncAllPendingEntries(user.uid);
        if (result.success > 0 || result.failed > 0) {
          setTimeout(async () => {
            try {
              const { invalidateDailySummaryCache } = await import('./hooks/useDailySummary');
              invalidateDailySummaryCache();
              window.dispatchEvent(new CustomEvent('daily-summary-refresh'));
            } catch (error) { console.error('[App] Failed to refresh UI after sync:', error); }
          }, 1000);
        }
      } catch (error) { console.error('[App] Failed to sync pending entries:', error); }
    };

    const parseRetryInterval = setInterval(async () => {
      if (navigator.onLine && user) {
        try {
          const { retryPendingParseEntries } = await import('./services/offlineSync.service');
          const result = await retryPendingParseEntries(user.uid);
          if (result.success > 0) {
            try {
              const { invalidateDailySummaryCache } = await import('./hooks/useDailySummary');
              invalidateDailySummaryCache();
            } catch (error) { console.error('[App] Failed to invalidate cache:', error); }
          }
        } catch (error) { console.error('[App] Periodic parse retry failed:', error); }
      }
    }, 5 * 60 * 1000);

    if ('serviceWorker' in navigator) { registerBackgroundSync().catch(console.error); }
    window.addEventListener('online', handleOnline);
    if (navigator.onLine) handleOnline();

    return () => { window.removeEventListener('online', handleOnline); clearInterval(parseRetryInterval); };
  }, [user, isDemoMode]);

  // iOS PWA: ensure html + body background matches app shell
  // Prevents white flash during rubber-band overscroll
  useEffect(() => {
    document.documentElement.style.background = '#fbf7f2';
    document.body.style.background = '#fbf7f2';
  }, []);

  // Detect standalone PWA mode (Add to Home Screen)
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  );

  const mainPaths = ['/home', '/nutrition', '/workouts', '/body-stats', '/glp1-hub'];
  const currentIndex = mainPaths.indexOf(location.pathname);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  function handleTouchStart(e: React.TouchEvent<HTMLDivElement>) {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }

  function handleTouchEnd(e: React.TouchEvent<HTMLDivElement>) {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-horizontal-scroll]')) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) < 100 || Math.abs(dx) < Math.abs(dy) * 1.5 || currentIndex === -1 || isAuthScreen) return;
    if (dx < 0 && currentIndex < mainPaths.length - 1) { demoNavigate(mainPaths[currentIndex + 1]); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    else if (dx > 0 && currentIndex > 0) { demoNavigate(mainPaths[currentIndex - 1]); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  }

  // ============================================================
  // RENDER: Splash overlay sits ON TOP of the shell.
  // Shell always renders underneath — gates decide what shows.
  // This means layout + nav appear as soon as splash fades.
  // ============================================================

  return (
    <UpgradePromptProvider>
      {/* Splash overlay — shown on top for 250-800ms */}
      {splashVisible && <AppSplash />}

      {/* App shell — always renders underneath splash */}
      <div className={isStandalone ? 'flex flex-col' : 'flex min-h-[100dvh] flex-col'}
        style={{
          ...(isStandalone ? { height: '100dvh', overflow: 'hidden' } : {}),
          background: '#fbf7f2',
          overscrollBehavior: 'none',
          touchAction: 'pan-y',
        }}>
        <div className="flex-1" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
          style={{
            // Standalone PWA: inner div owns scroll (body is locked via CSS)
            // Desktop / browser: overflow visible so #root / document owns scroll,
            // allowing mouse wheel and trackpad to work normally
            ...(isStandalone ? {
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'none',
              overscrollBehaviorY: 'none',
            } : {
              overflowY: 'visible',
            }),
            paddingBottom: (isAuthScreen || location.pathname.startsWith('/onboarding')) ? 0 : 'calc(var(--nav-height, 70px) + env(safe-area-inset-bottom, 0px) + 6px)',
          }}>
          {isDemoMode && (isPharmacyDemo() ? <PharmacyDemoBanner /> : <DemoBanner />)}
          <div className={isDemoMode ? 'pt-12' : ''}>
            {/* Don't render routes until auth resolves — prevents crashes on user-dependent screens */}
            {loading ? <AppSplash /> : element}
          </div>
          {showUpdatePrompt && (
            <UpdatePrompt onUpdate={async () => { setShowUpdatePrompt(false); await applyUpdate(); }} onDismiss={() => setShowUpdatePrompt(false)} />
          )}
        </div>
        {/* Nav mask — solid bg behind nav, above scrolling content (z-49) */}
        {!isAuthScreen && !location.pathname.startsWith('/onboarding') && (
          <div
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              height: 'calc(var(--nav-height, 70px) + env(safe-area-inset-bottom, 0px) + 6px)',
              background: '#fbf7f2',
              zIndex: 49,
              pointerEvents: 'none',
            }}
          />
        )}
        {!isAuthScreen && !location.pathname.startsWith('/onboarding') && <BottomNav />}
      </div>
    </UpgradePromptProvider>
  );
}

declare global {
  interface Window {
    __CORETRAK_SPLASH_TIMEOUT?: ReturnType<typeof setTimeout>;
  }
}

export default App;
